import { useTranslation } from "@sagot/i18n";
import { ChoiceChips, Icon, Segmented, Textarea, toast } from "@sagot/ui";
import { useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { AnswerLength, Tone } from "../shared/types";
import { LIMITS, TONES, type SettingsValues } from "./settings-model";
import { SettingsCard } from "./settings-ui";
import { usePromptPreview } from "./use-agent-settings";

const LENGTHS: readonly AnswerLength[] = ["short", "medium", "detailed"];
const isTone = (value: string): value is Tone => (TONES as readonly string[]).includes(value);

function Variables({ agentId, onInsert, disabled }: { agentId: string; onInsert: (variable: string) => void; disabled: boolean }) {
  const { t } = useTranslation();
  const { control } = useFormContext<SettingsValues>();
  const instructions = useWatch({ control, name: "instructions" });
  const preview = usePromptPreview(agentId, instructions);
  const variables = Object.entries(preview.data?.variables ?? {});
  const unknown = preview.data?.unknown ?? [];
  if (variables.length === 0 && unknown.length === 0) return null;
  return (
    <div className="rounded-md border border-line bg-surface-2 p-3.5">
      <div className="flex items-center gap-1.5 text-xs font-bold text-fg">
        <Icon name="hash" className="size-3.5 text-primary-ink" />
        {t("build.agentSettings.instructions.variables")}
        <span className="font-medium text-fg-subtle">{t("build.agentSettings.instructions.variablesHint")}</span>
      </div>
      <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {variables.map(([name, value]) => {
          const token = `{{${name}}}`;
          return (
            <button key={name} type="button" disabled={disabled} onClick={() => { onInsert(token); }} className="rounded-md border border-line bg-surface px-3 py-2 text-left hover:border-primary disabled:opacity-60">
              <span className="block font-mono text-xs font-medium text-primary-ink">{token}</span>
              <span className="block truncate text-[11px] text-fg-subtle">{value || t("build.agentSettings.instructions.variableEmpty")}</span>
            </button>
          );
        })}
      </div>
      {unknown.length > 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-warning" role="status">
          <Icon name="alert-triangle" className="size-3.5" />
          {t("build.agentSettings.instructions.unknown", { names: unknown.map((u) => `{{${u}}}`).join(", ") })}
        </p>
      ) : null}
    </div>
  );
}

export function InstructionsTab({ agentId, disabled }: { agentId: string; disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const instructions = useWatch({ control: form.control, name: "instructions" });
  const area = useRef<HTMLTextAreaElement | null>(null);
  const field = form.register("instructions");
  const insert = (token: string) => {
    const el = area.current;
    const start = el?.selectionStart ?? instructions.length;
    const end = el?.selectionEnd ?? start;
    const next = `${instructions.slice(0, start)}${token}${instructions.slice(end)}`.slice(0, LIMITS.instructionsMax);
    form.setValue("instructions", next, { shouldDirty: true });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
    toast.info(t("build.agentSettings.instructions.inserted", { variable: token }));
  };
  return (
    <>
      <SettingsCard title={t("build.agentSettings.instructions.role")} hint={t("build.agentSettings.instructions.roleHint")}>
        <label htmlFor="agent-instructions" className="sr-only">{t("build.agentSettings.instructions.promptLabel")}</label>
        <Textarea
          id="agent-instructions"
          rows={18}
          maxLength={LIMITS.instructionsMax}
          className="font-mono text-[12.5px]"
          disabled={disabled}
          aria-describedby="agent-instructions-count"
          {...field}
          ref={(el) => { field.ref(el); area.current = el; }}
        />
        <p id="agent-instructions-count" className="text-xs tabular-nums text-fg-subtle">
          {t("build.agentSettings.instructions.count", { count: instructions.length.toLocaleString(), max: LIMITS.instructionsMax.toLocaleString() })}
        </p>
        <Variables agentId={agentId} onInsert={insert} disabled={disabled} />
      </SettingsCard>
      <SettingsCard title={t("build.agentSettings.instructions.style")}>
        <div>
          <div className="text-sm font-semibold" id="agent-tone-label">{t("build.agentSettings.instructions.tone")}</div>
          <p className="text-xs text-fg-subtle">{t("build.agentSettings.instructions.toneHint")}</p>
          <Controller
            control={form.control}
            name="tones"
            render={({ field: tones }) => (
              <ChoiceChips
                multiple
                aria-label={t("build.agentSettings.instructions.tone")}
                className="mt-3"
                options={TONES.map((tone) => ({ value: tone, label: t(`build.agentSettings.instructions.tones.${tone}`), disabled: disabled || (!tones.value.includes(tone) && tones.value.length >= LIMITS.tonesMax) }))}
                value={tones.value}
                onValueChange={(next: string[]) => { tones.onChange(next.filter(isTone).slice(0, LIMITS.tonesMax)); }}
              />
            )}
          />
        </div>
        <div className="divider" />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold" id="agent-length-label">{t("build.agentSettings.instructions.length")}</div>
            <p className="text-xs text-fg-subtle">{t("build.agentSettings.instructions.lengthHint")}</p>
          </div>
          <Controller
            control={form.control}
            name="answer_length"
            render={({ field: length }) => (
              <Segmented aria-label={t("build.agentSettings.instructions.length")} value={length.value} onValueChange={(v) => { if (!disabled) length.onChange(v); }} options={LENGTHS.map((l) => ({ value: l, label: t(`build.agentSettings.instructions.lengths.${l}`) }))} />
            )}
          />
        </div>
      </SettingsCard>
    </>
  );
}
